/**
 * Fiche de travail — contrat partagé (spec §4, chantier 2026-09-09).
 *
 * Assemblage d'UN travail Vevidental pour l'écran `/travaux/<code>` : dates,
 * nature/dents/teinte, instructions labo, cabinet/praticien CRM, lien vers le
 * portail Vevidental, dossier patient (Partage) et action de relance en cours.
 * La fonction pure qui remplit ce contrat (`assemblerFiche`) vit côté serveur
 * (`apps/bff/src/travaux/fiche.ts`) — ce fichier ne porte que la FORME.
 */
import { z } from "zod";
import { AttenteSchema } from "./attentes";
import { ActionCaseSchema } from "./relances";
import { IsoDate } from "./primitives";
import { LienVevidentalSchema } from "./vevidental";

export const FichierFicheSchema = z.object({
  id: z.string(),
  nom: z.string(),
  type: z.enum(["image", "autre"]),
  urlApercu: z.string().nullable(),
  urlOuverture: z.string(),
});
export type FichierFiche = z.infer<typeof FichierFicheSchema>;

export const FicheTravailSchema = z.object({
  code: z.string(),
  nature: z.string().nullable(),
  statut: z.string().nullable(),
  /** Absente de l'API `/works` — toujours `null` (ruling 2026-09-09). */
  teinte: z.string().nullable(),
  dents: z.string().nullable(),
  entreLe: IsoDate.nullable(),
  sortiePrevueLe: IsoDate.nullable(),
  sortieDepassee: z.boolean(),
  instructions: z.string().nullable(),
  cabinet: z.object({ id: z.string().nullable(), nom: z.string() }).nullable(),
  praticien: z.object({ id: z.string().nullable(), nom: z.string() }).nullable(),
  lienVevidental: LienVevidentalSchema,
  dossier: z.object({ casId: z.string(), patientNom: z.string(), fichiers: z.array(FichierFicheSchema) }).nullable(),
  action: ActionCaseSchema.nullable(),
  /** L'attente OUVERTE du dentiste sur ce travail (spec 11/09) — `null` sinon. */
  attente: AttenteSchema.nullable(),
  /**
   * Le délai de fabrication à PROPOSER quand on déclare une attente, en jours
   * ouvrés : médiane apprise sur la famille (≥ 3 cas), sinon médiane mesurée
   * chez Vevidental. `null` = rien de mesurable — le champ reste vide et
   * l'utilisateur saisit, plutôt qu'un chiffre que personne n'a choisi.
   */
  delaiPropose: z.number().int().nullable(),
  /**
   * Le module « attente du dentiste » est-il ALLUMÉ ? (drapeau `attentes`).
   *
   * Le serveur est la source unique : l'écran ne relit pas le drapeau par une
   * seconde route, il lit ce booléen. Un état déduit deux fois dérive (leçon
   * du 03/09 : « les deux interrupteurs sont fermés » — ils étaient ouverts).
   *
   * ⚠ OPTIONNEL À DESSEIN, et c'est provisoire. Le serveur le remplit TOUJOURS
   * (`assemblerFiche`). `.default(false)` ne rendrait pas la propriété
   * facultative pour autant — `z.infer` est le type de SORTIE, donc `boolean`
   * requis (mesuré : `tsc` du front refusait alors ses deux décors). La vague B,
   * qui touche l'écran et ses décors, doit les rendre obligatoires.
   */
  attentes: z.boolean().optional(),
  /**
   * Le cabinet du travail est-il apparié à une fiche CRM ?
   *
   * ⚠ Ruling du contrôleur (11/09) : ce booléen vaut « `cabinetCrm` n'est pas
   * `null` ». Il ne dit donc PAS que les réveils pourront partir — `passerUn`
   * (`attentes/reveils.ts:147`) exige, lui, un `cabinet.id` non nul.
   *
   * Optionnel pour la même raison que `attentes` ci-dessus (vague B).
   */
  cabinetApparie: z.boolean().optional(),
});
export type FicheTravail = z.infer<typeof FicheTravailSchema>;
